import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import { faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
	getLicenceDetails,
	resendLicenceInvite,
	setLicenceExpiration,
	setLicenceState,
} from "../../api";
import { DataFormFieldProvider } from "../../contexts/dataFormFieldContext";
import {
	decrementLoaderCount,
	incrementLoaderCount,
} from "../../store/actions";
import {
	formatLocaleDateAndTimeString,
	formatLocaleDateString,
} from "../../utils";
import { BlockInput } from "../blockInput";
import { FieldDate } from "../dataForm/fieldDate";
import { RibbonButtons } from "../dataTable/dataTableRibbonButtons";
import { RefreshButton } from "../dataTable/refreshButton";
import { ReadonlyValueTable } from "../readonlyValueTable";
import { UserNameWithEmail } from "../userNameWithEmail";

// TODO STOP DEVS FROM EDITING HERE
export const LicenceModal = ({
	initialModalData,
	isModalOpen,
	setIsModalOpen,
	refreshTable,
}) => {
	const [licence, setCurrentModalData] = useState(
		JSON.parse(JSON.stringify(initialModalData))
	);
	const { loaderCount } = useSelector((state) => state.app);
	const dispatch = useDispatch();

	const handleClose = () => {
		setIsModalOpen(false);
	};

	const firstUpdate = useRef(true);

	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}

		setCurrentModalData(JSON.parse(JSON.stringify(initialModalData)));

		setIsModalOpen(true);
	}, [initialModalData]);

	const changeDateClick = async () => {
		dispatch(incrementLoaderCount());
		try {
			await setLicenceExpiration({
				licenceId: licence.id,
				expirationUtc: licence.licenceExpirationUtc,
			});
			await refreshTable();
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(decrementLoaderCount());
		}
		handleClose();
	};

	const reinstateClick = async () => {
		dispatch(incrementLoaderCount());
		try {
			await setLicenceState({
				licenceId: licence.id,
				state: licence.isRevoked ? 0 : 1,
			});
			await refreshTable();
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(decrementLoaderCount());
		}
		handleClose();
	};

	const deleteClick = async () => {
		dispatch(incrementLoaderCount());
		try {
			await setLicenceState({
				licenceId: licence.id,
				state: 2,
			});
			await refreshTable();
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(decrementLoaderCount());
		}
		handleClose();
	};

	const refreshLicence = async () => {
		dispatch(incrementLoaderCount());
		try {
			const licenceData = await getLicenceDetails({
				licenceId: licence.id,
			});
			setCurrentModalData({
				...licence,
				licenceData,
			});
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(decrementLoaderCount());
		}
	};

	const resendInvite = async () => {
		dispatch(incrementLoaderCount());
		try {
			await resendLicenceInvite({
				licenceId: licence.id,
			});
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(decrementLoaderCount());
		}
	};

	const tableRowsSetting = [
		{
			label: "User",
			accessor: ({ user }) => <UserNameWithEmail {...user} />,
		},
		{
			label: "Organisation",
			accessor: ({ org }) => org?.name,
		},
		{
			label: "Subscription",
			accessor: ({ subscription }) => subscription?.name,
		},
		{
			label: "Subscription Expiry",
			accessor: ({ subExpirationUtc }) =>
				formatLocaleDateString(subExpirationUtc),
		},
		{
			label: "Assigned By",
			accessor: ({ assignedByUser }) => (
				<UserNameWithEmail {...assignedByUser} />
			),
		},
		{
			label: "Assigned Date",
			accessor: ({ assignedDateUtc }) =>
				formatLocaleDateString(assignedDateUtc),
		},
		{
			label: "Licence Code",
			accessor: ({ id }) => id,
		},
		{
			label: "State",
			accessor: ({ state }) => state,
		},
		{
			label: "Last Used",
			accessor: ({ lastUsedUtc }) =>
				formatLocaleDateAndTimeString(lastUsedUtc),
		},
		{
			label: "Last Used Version",
			accessor: ({ lastUsedAgentString }) => lastUsedAgentString,
		},
		{
			label: "Active Machine",
			accessor: ({ activeMachine }) => activeMachine || null,
		},
		{
			label: "Last Activated",
			accessor: ({ lastActivatedUtc }) =>
				formatLocaleDateAndTimeString(lastActivatedUtc),
		},
	];

	return (
		<Modal
			as={Modal.Dialog}
			centered
			show={isModalOpen}
			onHide={handleClose}
			className="modal-lg"
		>
			<BlockInput blocking={loaderCount > 0} />
			<Modal.Header>
				<Modal.Title className="h6">
					Edit Licence for{" "}
					{licence?.offering?.name ||
						licence?.subscription?.offering?.name}
				</Modal.Title>
				<RibbonButtons>
					<Button
						onClick={resendInvite}
						key={`btn-resend`}
						variant="outline-primary"
						size="sm"
						aria-label="Close"
					>
						Resend Invite
					</Button>
					<RefreshButton onClick={refreshLicence} />
					<Button
						onClick={handleClose}
						key={`btn-close`}
						variant="outline-primary"
						size="sm"
						aria-label="Close"
					>
						<FontAwesomeIcon icon={faX} />
					</Button>
				</RibbonButtons>
			</Modal.Header>
			<Modal.Body>
				<ReadonlyValueTable
					className="mb-2"
					settings={tableRowsSetting}
					data={licence}
				/>
				<DataFormFieldProvider
					fieldSetting={{
						title: "Licence Specific Expiration Date (Optional)",
						tooltip:
							"This allows you to further restrict the amount of time people can use their licences e.g. a 30 day trial",
					}}
				>
					<FieldDate
						setValue={(newDate) =>
							setCurrentModalData((data) => {
								data.licenceExpirationUtc = newDate;
								return data;
							})
						}
						fieldData={licence.licenceExpirationUtc}
						placeholder="N/A"
					/>
				</DataFormFieldProvider>
			</Modal.Body>
			<Modal.Footer>
				<div className="ms-auto">
					{licence.isRevoked ? (
						<Button variant="danger" onClick={deleteClick}>
							Delete
						</Button>
					) : (
						""
					)}
					<Button
						className="mx-2"
						variant="secondary"
						onClick={reinstateClick}
					>
						{licence.isRevoked ? "Reinstate" : "Revoke"}
					</Button>
					<Button
						className="mx-2"
						variant="primary"
						onClick={changeDateClick}
						// disable change date till after date changes
					>
						Save Expiration Date Changes
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};
